import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import logoAsset from "@/assets/logo-villalobos.png.asset.json";


const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services", dropdown: "services" },
  { label: "Locations", href: "/locations", dropdown: "locations" },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
];

const Navbar = () => {
  const { openQuoteForm } = useQuoteForm();
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openDropdown = (id: string) => {
    clearTimeout(hoverTimeout.current);
    setActiveDropdown(id);
  };

  const closeDropdown = () => {
    hoverTimeout.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  const mobileMenu = (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-[998] md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-card z-[999] md:hidden transform transition-transform duration-300 ease-in-out shadow-2xl ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
            <img src={logoAsset.url} alt="Villalobos Prime Services LLC" className="h-12 w-auto rounded-xl" />
          </Link>
          <button onClick={() => setOpen(false)} className="p-1 text-foreground/70 hover:text-foreground" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="flex flex-col px-4 py-4 gap-1 overflow-y-auto scrollbar-hide h-[calc(100%-72px)]">
          {navLinks.map((link) =>
            link.dropdown === "services" ? (
              <li key={link.href}>
                <div className="flex items-center justify-between py-3">
                  <Link to={link.href} onClick={() => setOpen(false)} className="font-heading text-lg tracking-wide text-foreground/80 hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="p-1 text-foreground/80"
                    aria-label="Toggle Services submenu"
                    aria-expanded={mobileServicesOpen}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <ul className="pl-4 pb-2 space-y-1">
                    {services.map((s) => (
                      <li key={s.slug}>
                        <Link to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-foreground/70 hover:text-primary transition-colors">
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : link.dropdown === "locations" ? (
              <li key={link.href}>
                <div className="flex items-center justify-between py-3">
                  <Link to={link.href} onClick={() => setOpen(false)} className="font-heading text-lg tracking-wide text-foreground/80 hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                  <button
                    onClick={() => setMobileLocationsOpen(!mobileLocationsOpen)}
                    className="p-1 text-foreground/80"
                    aria-label="Toggle Locations submenu"
                    aria-expanded={mobileLocationsOpen}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileLocationsOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileLocationsOpen && (
                  <ul className="pl-4 pb-2 space-y-1 scrollbar-hide max-h-60 overflow-y-auto">
                    {locations.map((loc) => (
                      <li key={loc.slug}>
                        <Link to={`/locations/${loc.slug}`} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-foreground/70 hover:text-primary transition-colors">
                          {loc.name}, {loc.state}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li key={link.href}>
                <Link to={link.href} onClick={() => setOpen(false)} className="font-heading text-lg tracking-wide text-foreground/80 hover:text-primary transition-colors block py-3">
                  {link.label}
                </Link>
              </li>
            )
          )}
          <li className="pt-4">
            <Button variant="default" size="sm" className="w-full" onClick={() => { openQuoteForm(); setOpen(false); }}>
              Get Started
            </Button>
          </li>
        </ul>
      </div>
    </>
  );

  return (
    <>
      <nav
        className={`site-header ${scrolled ? "site-header--scrolled" : ""}`}
        aria-label="Primary"
      >
        <div className="header-shell">
          <div className="header-inner container-max flex items-center justify-between px-4 sm:px-6 py-2.5">
          <Link to="/" className="header-logo flex items-center gap-2" aria-label="Home">
            <img src={logoAsset.url} alt="Villalobos Prime Services LLC" className="h-14 w-auto rounded-xl" />
          </Link>
          <ul className="hidden md:flex items-center gap-1 lg:gap-2">
            {navLinks.map((link) =>
              link.dropdown ? (
                <li
                  key={link.href}
                  className="relative"
                  onMouseEnter={() => openDropdown(link.dropdown!)}
                  onMouseLeave={closeDropdown}
                >
                  <Link
                    to={link.href}
                    data-active={location.pathname.startsWith(link.href) && link.href !== "/"}
                    className="nav-pill font-heading text-[15px] tracking-wide text-foreground/85 hover:text-primary inline-flex items-center gap-1"
                  >
                    {link.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${activeDropdown === link.dropdown ? "rotate-180" : ""}`} />
                  </Link>
                  {activeDropdown === link.dropdown && link.dropdown === "services" && (
                    <div className="absolute top-full left-0 pt-3 z-[60]">
                      <div className="header-dropdown w-64 py-2">
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            className="block px-4 py-2.5 text-sm text-popover-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="font-semibold">{s.title}</span>
                            <span className="block text-xs text-muted-foreground">{s.subtitle}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeDropdown === link.dropdown && link.dropdown === "locations" && (
                    <div className="absolute top-full left-0 pt-3 z-[60]">
                      <div className="header-dropdown py-2 w-64 max-h-80 overflow-y-auto scrollbar-hide">
                        {locations.map((loc) => (
                          <Link
                            key={loc.slug}
                            to={`/locations/${loc.slug}`}
                            className="block px-4 py-2.5 text-sm text-popover-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="font-semibold">{loc.name}, {loc.state}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ) : (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    data-active={link.href === "/" ? location.pathname === "/" : location.pathname.startsWith(link.href)}
                    className="nav-pill font-heading text-[15px] tracking-wide text-foreground/85 hover:text-primary"
                  >
                    {link.label}
                  </Link>
                </li>
              )
            )}
            <li className="hidden lg:block pl-2">
              <Button variant="default" size="sm" onClick={openQuoteForm} className="rounded-full px-5 btn-glow">
                Get Free Estimate
              </Button>
            </li>
          </ul>
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden text-foreground p-2 rounded-full hover:bg-primary/10 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <Menu className="h-6 w-6" />
          </button>
          </div>
        </div>
      </nav>
      {createPortal(mobileMenu, document.body)}
    </>
  );
};

export default Navbar;
